import { useEffect } from 'react';
import { convertToLeadTime, convertToRequestBufferDuration } from '@frontend/api-appointment-types';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  Alert,
  ContentLoader,
  DropdownField,
  FormRow,
  Heading,
  Modal,
  NakedButton,
  NumberField,
  Text,
  useForm,
  useModalControl,
} from '@frontend/design-system';
import * as styles from '../AppointmentTypeSettings/styles';

interface Props {
  numOfApptTypes: number;
  apptReqConfigIsLoading: boolean;
  apptReqConfigFetchError: boolean;
  requestBufferDuration: number;
  updateLeadTime: (requestBufferDuration: number) => void;
}

export const LeadTime = ({
  numOfApptTypes,
  apptReqConfigIsLoading,
  apptReqConfigFetchError,
  requestBufferDuration,
  updateLeadTime,
}: Props) => {
  const { t } = useTranslation('schedule');
  const helpModal = useModalControl();

  const { values, getFieldProps, seedValues } = useForm({
    fields: {
      leadTimeValue: {
        type: 'number',
      },
      timeUnit: {
        type: 'dropdown',
      },
    },
  });

  // Update form values when requestBufferDuration changes
  useEffect(() => {
    const { days, hours } = convertToLeadTime(requestBufferDuration);
    seedValues({
      leadTimeValue: days > 0 ? `${days}` : `${hours}`,
      timeUnit: days > 0 ? 'days' : 'hours',
    });
  }, [requestBufferDuration, seedValues]);

  // Update lead time when value or type changes
  const updateValues = (timeUnit?: 'days' | 'hours') => {
    const leadTimeValue = values.leadTimeValue;
    const timeUnitValue = timeUnit || values.timeUnit;
    let buffer: number;

    if (timeUnitValue === 'days') {
      buffer = convertToRequestBufferDuration(leadTimeValue, '0');
    } else {
      buffer = convertToRequestBufferDuration('0', leadTimeValue);
    }
    updateLeadTime(buffer);
  };

  return (
    <>
      <form name={t('appointment request lead time')}>
        <Heading level={3} css={styles.cardTextSpacing}>
          <NakedButton {...helpModal.triggerProps}>
            {t('Appointment Lead Time')} <Icon name='help' size={14} />
          </NakedButton>
        </Heading>

        {apptReqConfigFetchError && (
          <Alert type='error' css={styles.alertMargin}>
            {t('Unable to fetch appointment lead time. Please try again later.')}
          </Alert>
        )}

        {numOfApptTypes === 0 && (
          <Alert type='warning' css={styles.alertMargin}>
            {t('Please add an appointment type before updating the appointment lead time.')}
          </Alert>
        )}

        {!apptReqConfigIsLoading && (
          <FormRow css={styles.leadTimeRow}>
            <NumberField
              {...getFieldProps('leadTimeValue')}
              css={styles.leadTimeField}
              label={t('Duration')}
              onBlur={(e: any) => {
                getFieldProps('leadTimeValue').onBlur(e);
                updateValues();
              }}
            />
            <DropdownField label={t('Time Unit')} {...getFieldProps('timeUnit')} css={styles.leadTimeField}>
              <DropdownField.Option
                key={'days'}
                value='days'
                onClick={() => {
                  updateValues('days');
                }}
              >
                {t('Days')}
              </DropdownField.Option>

              <DropdownField.Option
                key={'hours'}
                value='hours'
                onClick={() => {
                  updateValues('hours');
                }}
              >
                {t('Hours')}
              </DropdownField.Option>
            </DropdownField>
          </FormRow>
        )}
      </form>

      <ContentLoader show={apptReqConfigIsLoading} />

      <Modal {...helpModal.modalProps}>
        <Modal.Header>{t('Appointment Lead Time')}</Modal.Header>
        <Modal.Body>
          <Text color='light' css={styles.cardTextSpacing}>
            {t(
              'The time you select will be the soonest a patient can request an appointment. This is a "safety" window to prevent same-day or same-hour requests.'
            )}
          </Text>
          <Text color='light' css={styles.cardTextSpacing}>
            <Text as='span' weight='bold'>
              {t('Note:')}
            </Text>
            {t(' Daily windows of schedule availability are determined in Appointment Hours.')}
          </Text>
        </Modal.Body>
        <Modal.Actions onPrimaryClick={helpModal.closeModal} primaryLabel={t('Done')} />
      </Modal>
    </>
  );
};
